import { useEffect, useRef, useState, type FC } from 'react';
import { format } from 'date-fns';
import { LuRefreshCw, LuTrash2 } from 'react-icons/lu';
import RecordPanel from './RecordPanel';
import SecaoHistorico from './historico/SecaoHistorico';
import Select from './Select';
import type { Contact } from '../services/ContactService';

export interface AppointmentFormData {
    id?: string;
    title: string;
    /** yyyy-MM-dd */
    date: string;
    /** HH:mm */
    time: string;
    /** HH:mm — desde 12/09 a duração sai do formulário, não é mais +1h fixo. */
    endTime: string;
    contatoId: string;
    description: string;
    sincronizarGoogle: boolean;
}

interface AppointmentPanelProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: AppointmentFormData) => void;
    /** Só aparece na edição; fica na ponta esquerda do rodapé, longe do Salvar. */
    onDelete?: (id: string) => void;
    /** Dia selecionado na Agenda — vira a data do compromisso novo. */
    initialDate?: Date;
    /** Hora da faixa clicada na linha do dia. Sem ela, 09:00. */
    initialTime?: string;
    contacts?: Contact[];
    editingAppointment?: any;
    googleConectado?: boolean;
    salvando?: boolean;
}

/** "Marina Duarte" → "MD"; "Ricardo" → "RI". */
const iniciais = (nome: string) => {
    const partes = nome.trim().split(/\s+/).filter(Boolean);
    if (partes.length === 0) return '';
    if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
    return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
};

/** Soma minutos a "HH:mm" e devolve "HH:mm", sem passar do fim do dia. */
const somaMinutos = (hora: string, minutos: number) => {
    const [h, m] = hora.split(':').map(Number);
    const total = Math.min(h * 60 + m + minutos, 23 * 60 + 59);
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * Painel de agendamento — substitui a `AppointmentModal`, a última peça da
 * Agenda anterior ao redesign.
 *
 * Desenho fechado em 2026-09-12, ver
 * docs/designsystem/propostas/agenda-agendamento.html. Duas decisões dele que
 * explicam o que parece faltar aqui:
 *
 * - **Só existe o modo edição.** O molde do `RecordPanel` prevê ver + editar, e
 *   o desenho tinha os dois; ele preferiu que o clique num compromisso caia
 *   direto no formulário, como era antes. Por isso `editing` é sempre `true` e
 *   não há barra de ações.
 * - **O interruptor do Google só aparece na criação.** Na edição ele não tem
 *   para onde ir: `UpdateEventDTO` não carrega `sincronizarGoogle`, então a
 *   caixa que existia antes aceitava o clique e descartava o valor.
 */
const AppointmentPanel: FC<AppointmentPanelProps> = ({
    isOpen, onClose, onSave, onDelete, initialDate, initialTime,
    contacts = [], editingAppointment, googleConectado = false, salvando = false,
}) => {
    const [title, setTitle] = useState('');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('09:00');
    const [endTime, setEndTime] = useState('10:00');
    const [contatoId, setContatoId] = useState('');
    const [description, setDescription] = useState('');
    const [sincronizarGoogle, setSincronizarGoogle] = useState(false);
    const [erros, setErros] = useState<Record<string, string>>({});
    const tituloRef = useRef<HTMLInputElement>(null);

    const editando = !!editingAppointment;
    const doGoogle = !!editingAppointment?.googleEventId;

    useEffect(() => {
        if (!isOpen) return;
        setErros({});

        if (editingAppointment) {
            const inicio: Date = editingAppointment.date;
            const horaInicio = editingAppointment.time as string;
            setTitle(editingAppointment.title || '');
            setDate(format(inicio, 'yyyy-MM-dd'));
            setTime(horaInicio);
            setEndTime(editingAppointment.endTime || somaMinutos(horaInicio, 60));
            setContatoId(editingAppointment.contatoId?.toString() || '');
            setDescription(editingAppointment.description || '');
            setSincronizarGoogle(!!editingAppointment.sincronizadoGoogle);
            return;
        }

        const hora = initialTime || '09:00';
        setTitle('');
        setDate(format(initialDate || new Date(), 'yyyy-MM-dd'));
        setTime(hora);
        setEndTime(somaMinutos(hora, 60));
        setContatoId('');
        setDescription('');
        setSincronizarGoogle(false);
        // O título é o único campo que o clique na faixa não preenche.
        window.setTimeout(() => tituloRef.current?.focus(), 60);
    }, [isOpen, initialDate, initialTime, editingAppointment]);

    const salvar = () => {
        const novos: Record<string, string> = {};
        if (!title.trim()) novos.title = 'Dê um nome ao compromisso.';
        if (!date) novos.date = 'Escolha a data.';
        if (endTime <= time) novos.endTime = 'O término tem de ser depois do início.';
        setErros(novos);
        if (Object.keys(novos).length > 0) return;

        onSave({
            id: editingAppointment?.id,
            title: title.trim(),
            date,
            time,
            endTime,
            contatoId,
            description,
            sincronizarGoogle,
        });
    };

    const contato = contacts.find(c => c.contatoId.toString() === contatoId);

    const opcoesContato = [
        { value: '', label: 'Sem contato — compromisso geral' },
        ...contacts.map(c => ({
            value: c.contatoId.toString(),
            label: c.nome,
            avatar: iniciais(c.nome),
            meta: c.telefone || undefined,
        })),
    ];

    return (
        // `editing` fica sempre ligado: o painel aqui tem um modo só. Com ele
        // a linha de apoio do cabeçalho é sempre "Editando…" — por isso não
        // passo `subtitle`, que o componente ignoraria.
        <RecordPanel
            open={isOpen}
            onClose={onClose}
            editing
            initials={editando && contato ? iniciais(contato.nome) : undefined}
            title={editando ? (editingAppointment.title || 'Compromisso') : 'Novo agendamento'}
            footer={
                <>
                    {editando && onDelete && (
                        <button type="button" className="rp-btn is-danger" disabled={salvando}
                            onClick={() => onDelete(editingAppointment.id)}>
                            <LuTrash2 size={13} /> Excluir
                        </button>
                    )}
                    <button type="button" className="rp-btn" onClick={onClose} disabled={salvando}>
                        Cancelar
                    </button>
                    <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={salvando}>
                        Salvar
                    </button>
                </>
            }
        >
            <section className="rp-sec">
                <div className="rp-field">
                    <label className="rp-label" htmlFor="ag-titulo">Título do compromisso</label>
                    <input id="ag-titulo" ref={tituloRef}
                        className={`rp-input ${erros.title ? 'has-error' : ''}`}
                        placeholder="Ex: visita técnica"
                        value={title} onChange={e => setTitle(e.target.value)} />
                    {erros.title && <p className="rp-error">{erros.title}</p>}
                </div>

                <div className="rp-field">
                    <label className="rp-label" htmlFor="ag-data">Data</label>
                    <input id="ag-data" type="date"
                        className={`rp-input ${erros.date ? 'has-error' : ''}`}
                        value={date} onChange={e => setDate(e.target.value)} />
                    {erros.date && <p className="rp-error">{erros.date}</p>}
                </div>

                <div className="rp-two">
                    <div className="rp-field">
                        <label className="rp-label" htmlFor="ag-inicio">Começa às</label>
                        <input id="ag-inicio" type="time" className="rp-input"
                            value={time}
                            onChange={e => {
                                const nova = e.target.value;
                                setTime(nova);
                                // O término acompanha o início enquanto ninguém o move na mão.
                                if (endTime <= nova) setEndTime(somaMinutos(nova, 60));
                            }} />
                    </div>
                    <div className="rp-field">
                        <label className="rp-label" htmlFor="ag-fim">Termina às</label>
                        <input id="ag-fim" type="time"
                            className={`rp-input ${erros.endTime ? 'has-error' : ''}`}
                            value={endTime} onChange={e => setEndTime(e.target.value)} />
                        {erros.endTime && <p className="rp-error">{erros.endTime}</p>}
                    </div>
                </div>
            </section>

            <section className="rp-sec">
                <div className="rp-field">
                    <label className="rp-label" htmlFor="ag-contato">Contato</label>
                    <Select id="ag-contato"
                        value={contatoId}
                        options={opcoesContato}
                        onChange={setContatoId}
                        searchFrom={8}
                        showOptionAccessory
                        placeholder="Sem contato — compromisso geral" />
                </div>

                <div className="rp-field">
                    <label className="rp-label" htmlFor="ag-detalhes">Detalhes</label>
                    <textarea id="ag-detalhes" className="rp-input is-area" rows={3}
                        placeholder="Pauta, endereço, o que levar…"
                        value={description} onChange={e => setDescription(e.target.value)} />
                </div>
            </section>

            {/* A origem é a última linha do formulário: na criação ela é uma
                escolha, na edição de um evento do Google é só informação. */}
            {doGoogle ? (
                <section className="rp-sec">
                    <div className="rp-toggle-row" style={{ borderTop: 'none', paddingTop: 0 }}>
                        <span className="rp-toggle-ico"><LuRefreshCw size={14} /></span>
                        <span className="rp-toggle-text">
                            <span className="rp-toggle-label">Sincronizado com o Google Calendar</span>
                            <span className="rp-toggle-hint">A alteração vai para a sua conta Google automaticamente</span>
                        </span>
                    </div>
                </section>
            ) : !editando && googleConectado ? (
                <section className="rp-sec">
                    <div className="rp-toggle-row" style={{ borderTop: 'none', paddingTop: 0 }}>
                        <span className="rp-toggle-ico"><LuRefreshCw size={14} /></span>
                        <span className="rp-toggle-text">
                            <span className="rp-toggle-label">Sincronizar com o Google Calendar</span>
                            <span className="rp-toggle-hint">Cria o evento também na sua conta Google</span>
                        </span>
                        <label className="rp-switch">
                            <input type="checkbox" checked={sincronizarGoogle}
                                onChange={e => setSincronizarGoogle(e.target.checked)} />
                            <span className="rp-switch-track" />
                            <span className="rp-switch-thumb" />
                        </label>
                    </div>
                </section>
            ) : null}

            {/* Histórico (fase 5): o painel só tem modo edição, então mora no fim do formulário,
                e só de compromisso que já existe. */}
            {editando && editingAppointment?.id && (
                <SecaoHistorico entidade="agenda" id={Number(editingAppointment.id)} />
            )}
        </RecordPanel>
    );
};

export default AppointmentPanel;
